import { useDirection } from "@radix-ui/react-direction";
import {
  Bell,
  BookOpen,
  Building2,
  CalendarDays,
  ChevronDown,
  CircleHelp,
  CreditCard,
  History,
  KeyRound,
  LogOut,
  Settings,
  SlidersHorizontal,
} from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useUnreadCount } from "@/api/notifications/useUnreadCount";
import { AvatarPlaceholder } from "@/components/icons";
import { User } from "@/components/icons/User";
import { useNotificationsDrawer } from "@/components/notifications/hooks/use-notifications-drawer";
import { TruncatedTextWithTooltip } from "@/components/TruncatedTextWithTooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PATHS } from "@/constants/paths";
import { useAuth } from "@/hooks/use-auth";
import { useCallAssistantFeaturesEnabled } from "@/hooks/use-call-assistant-features-enabled";
import { useExternalApiEnabled } from "@/hooks/use-external-api-enabled";
import { useIsBillingDisabled } from "@/hooks/use-is-billing-disabled";
import { useMainTour } from "@/hooks/use-main-tour";
import { useOnPremise } from "@/hooks/use-on-premise";
import { cn } from "@/lib/utils";

import {
  EProfileTab,
  ESettingsQueryParams,
  ESettingsSection,
} from "../settings/types";
import { LogoutConfirmationModal } from "./LogoutConfirmationModal";
import { ThemePicker } from "./ThemePicker";
import {
  USER_MENU_ROW_CLASS,
  USER_MENU_SECTION_START_CLASS,
} from "./user-menu-row";

interface IUserMenuProps {
  // NOTE: NSAI-4345 — the sidebar footer shows the name and email beside the
  // avatar and folds them away in the icon rail. Standalone headers, such as
  // the no-projects screen, keep the compact avatar + chevron.
  hasDetails?: boolean;
  // NOTE: NSAI-6401 — "folds them away in the icon rail" is the catch: with the
  // rail collapsed, `hasDetails` is still true but the capsule's name and email
  // are hidden by `group-data-[collapsible=icon]:hidden`. That is a CSS state on
  // the sidebar, and this menu renders in a portal outside it, so it cannot be
  // read from here — `MainSidebar` passes it down instead. Defaults to false for
  // the callers that have no sidebar at all.
  isRailCollapsed?: boolean;
}

export const UserMenu = memo((props: IUserMenuProps) => {
  // NOTE: Destructured here rather than in the parameter list so the arrow
  // signature stays on one line: wrapping it re-indents the whole component and
  // turns a fifteen-line change into a six-hundred-line diff.
  const { hasDetails = false, isRailCollapsed = false } = props;
  const dir = useDirection();

  const { isRunning, currentStep, userMenuStepIndex } = useMainTour();
  const { isOnPremise } = useOnPremise();
  const { isBillingDisabled } = useIsBillingDisabled();
  const { isExternalApiEnabled } = useExternalApiEnabled();
  const isCallAssistantFeaturesEnabled = useCallAssistantFeaturesEnabled();

  // NOTE: Number of tour STEPS whose target lives inside this dropdown
  // (notifications, settings, organizations, preferences, history, faq, docs +
  // conditional: api keys, calendars, billing). NSAI-4345 moved notifications
  // out to the header bell and the project selector in; NSAI-6259 undid both,
  // sending the selector back to the header and bringing notifications back
  // here — one row out, one row in, so the count is unchanged again and only
  // its membership moved. The theme picker arrived in the same pass and is NOT
  // counted: it is a row of the menu but has no tour step of its own.
  let userMenuItemCount = 7;
  if (isExternalApiEnabled) userMenuItemCount += 1;
  if (isCallAssistantFeaturesEnabled) userMenuItemCount += 1;
  if (!isOnPremise && !isBillingDisabled) userMenuItemCount += 1;
  const isInUserMenuRange =
    currentStep >= userMenuStepIndex &&
    currentStep < userMenuStepIndex + userMenuItemCount;

  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const { user, signOut } = useAuth();
  const { openDrawer: openNotificationsDrawer } = useNotificationsDrawer();
  const { data: unreadCountData } = useUnreadCount();
  const unreadCount = unreadCountData?.count ?? 0;
  const hasUnreadNotifications = unreadCount > 0;

  const { t } = useTranslation("common");
  const { t: tNotifications } = useTranslation("notifications");

  const handleLogoutClick = (): void => {
    setIsOpen(false);
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = (): void => {
    signOut();
  };

  const AvatarComponent = isOnPremise ? User : AvatarPlaceholder;

  const userDisplayName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  // NOTE: NSAI-6401 — whether the control this menu hangs off is already
  // showing the name, which decides whether the menu should show it too. Only
  // the expanded sidebar capsule does: the no-projects header is avatar +
  // chevron, and so is the capsule once the rail collapses to icons.
  const isNamedByTrigger = hasDetails && !isRailCollapsed;

  return (
    <>
      <DropdownMenu
        dir={dir}
        open={isOpen || isRunning}
        modal={!isRunning}
        onOpenChange={setIsOpen}
      >
        <DropdownMenuTrigger asChild>
          <button
            id="user-menu"
            data-testid="user-menu"
            className={cn(
              "flex items-center gap-1 group",
              hasDetails &&
                "w-full gap-2 rounded-xl p-1.5 transition-colors duration-200 ease-out hover:bg-sidebar-accent group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0",
            )}
          >
            <span className="relative inline-flex shrink-0">
              <AvatarComponent className="h-9 w-9 shrink-0" />
              {hasUnreadNotifications && (
                <span
                  aria-label="unread notifications"
                  data-testid="user-menu-unread-badge"
                  // NOTE: NSAI-5822 — the ring exists to cut the badge out of
                  // whatever it sits on. At the foot of the sidebar that is
                  // --sidebar-background, not --background: a page-coloured ring
                  // drew a pale halo around the badge on the tinted rail.
                  className={cn(
                    "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-[10px] leading-none font-semibold text-primary-foreground flex items-center justify-center ring-2",
                    hasDetails ? "ring-sidebar" : "ring-background",
                  )}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </span>
            {/* NOTE: NSAI-4345 — the account row lives at the foot of the
                sidebar, so it carries the name and email beside the avatar.
                Both, and the chevron, fold away in the collapsed icon rail. */}
            {hasDetails && (
              <span className="flex min-w-0 flex-1 flex-col items-start text-start group-data-[collapsible=icon]:hidden">
                <TruncatedTextWithTooltip
                  value={userDisplayName}
                  className="text-sm font-semibold leading-tight"
                  wrapperClassName="w-full min-w-0"
                />
                <TruncatedTextWithTooltip
                  value={user?.email ?? ""}
                  className="text-xs text-sidebar-foreground-secondary leading-tight"
                  wrapperClassName="w-full min-w-0"
                />
              </span>
            )}
            {/* NOTE: NSAI-6259 — the rail's ink rather than `--primary`, for
                the same reason as the sidebar trigger: a disclosure chevron is
                chrome, and in dark `--primary` is a pale lavender that made it
                the only coloured glyph in the account row. */}
            <ChevronDown
              className={cn(
                "shrink-0 text-sidebar-foreground transition-transform duration-200 h-5 w-5 group-data-[state=open]:rotate-180",
                hasDetails && "group-data-[collapsible=icon]:hidden",
              )}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          data-testid="user-menu-dropdown"
          align={hasDetails ? "start" : "end"}
          side={hasDetails ? "top" : "bottom"}
          sideOffset={10}
          className={cn(
            "min-w-56 max-w-56 overflow-x-hidden p-0",
            isRunning && !isInUserMenuRange && "opacity-0 pointer-events-none",
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {/* NOTE: NSAI-6401 — the name is printed by whichever surface is the
              only one showing it, never by both. When the expanded sidebar
              capsule carries name + email, a title inside the menu was the same
              string twice ten pixels apart, so this block stands down. When the
              trigger is avatar + chevron alone — the no-projects header, or the
              same capsule once the rail collapses to icons — this is the only
              place the account is named, and dropping it would leave the open
              menu anonymous.

              NOTE: NSAI-6259 follow-up — still gated on a non-empty name. The
              block was once unconditional, so an account with neither first nor
              last name drew an empty `pt-2 pb-2` strip: 16px of blank at the
              top of the menu with nothing in it. */}
          {!isNamedByTrigger && userDisplayName && (
            <div
              data-testid="user-menu-identity"
              className="flex min-w-0 max-w-full flex-col overflow-x-hidden px-4 pt-2 pb-2"
            >
              <TruncatedTextWithTooltip
                value={userDisplayName}
                className="text-sm font-semibold"
                wrapperClassName="w-full min-w-0"
              />
            </div>
          )}
          {/* NOTE: NSAI-6259 — theme and notifications took the slot the
              project selector left when it was promoted to the header. They sit
              directly under the identity block, where the selector used to be.

              NOTE: NSAI-6259 follow-up — the count is on this row too, not only
              on the avatar. The objection the move answered was that it was
              rendered on the avatar AND on a header bell, two unrelated
              controls in two corners; the avatar is this menu's own trigger, so
              the badge and the row it labels are the same control seen closed
              and open. Both read `unreadCount` — one `useUnreadCount` query,
              one cache key, invalidated by mark-as-read and by the
              notifications socket — so they can never disagree and they clear
              together the moment everything is read. */}
          <ThemePicker />
          <DropdownMenuItem
            id="notifications-menu-item"
            data-testid="user-menu-notifications"
            className={USER_MENU_ROW_CLASS}
            onClick={openNotificationsDrawer}
          >
            <Bell className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{tNotifications("title")}</span>
            {hasUnreadNotifications && (
              <span
                aria-label="unread notifications"
                data-testid="user-menu-notifications-badge"
                className="ms-auto min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-[10px] leading-none font-semibold text-primary-foreground flex items-center justify-center"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            id="settings-menu-item"
            data-testid="user-menu-settings"
            className={USER_MENU_SECTION_START_CLASS}
            asChild
          >
            <Link to={PATHS.SETTINGS}>
              <>
                <Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("user_menu.settings")}
              </>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            id="organizations-menu-item"
            data-testid="user-menu-organizations"
            className={USER_MENU_ROW_CLASS}
            asChild
          >
            <Link
              to={`${PATHS.SETTINGS}?${ESettingsQueryParams.SECTION}=${ESettingsSection.ORGANIZATION}`}
            >
              <>
                <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("user_menu.organizations")}
              </>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            id="preferences-menu-item"
            data-testid="user-menu-preferences"
            className={USER_MENU_ROW_CLASS}
            asChild
          >
            <Link
              to={`${PATHS.SETTINGS}?${ESettingsQueryParams.SECTION}=${ESettingsSection.PROFILE}&${ESettingsQueryParams.TAB}=${EProfileTab.PREFERENCES}`}
            >
              <>
                <SlidersHorizontal
                  className="h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                {t("user_menu.preferences")}
              </>
            </Link>
          </DropdownMenuItem>
          {isExternalApiEnabled && (
            <DropdownMenuItem
              id="api-keys-menu-item"
              data-testid="user-menu-api-keys"
              className={USER_MENU_ROW_CLASS}
              asChild
            >
              <Link
                to={`${PATHS.SETTINGS}?${ESettingsQueryParams.SECTION}=${ESettingsSection.PROFILE}&${ESettingsQueryParams.TAB}=${EProfileTab.API_KEYS}`}
              >
                <>
                  <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {t("user_menu.api_keys")}
                </>
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            id="history-menu-item"
            data-testid="user-menu-history"
            className={USER_MENU_ROW_CLASS}
            asChild
          >
            <Link
              to={`${PATHS.SETTINGS}?${ESettingsQueryParams.SECTION}=${ESettingsSection.PROFILE}&${ESettingsQueryParams.TAB}=${EProfileTab.HISTORY}`}
            >
              <>
                <History className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("user_menu.history")}
              </>
            </Link>
          </DropdownMenuItem>
          {isCallAssistantFeaturesEnabled && (
            <DropdownMenuItem
              id="calendars-menu-item"
              data-testid="user-menu-calendars"
              className={USER_MENU_ROW_CLASS}
              asChild
            >
              <Link
                to={`${PATHS.SETTINGS}?${ESettingsQueryParams.SECTION}=${ESettingsSection.PROFILE}&${ESettingsQueryParams.TAB}=${EProfileTab.CALENDAR}`}
              >
                <>
                  <CalendarDays
                    className="h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  {t("user_menu.calendars")}
                </>
              </Link>
            </DropdownMenuItem>
          )}
          {!isOnPremise && !isBillingDisabled && (
            <DropdownMenuItem
              id="billing-menu-item"
              data-testid="user-menu-billing"
              className={USER_MENU_ROW_CLASS}
              asChild
            >
              <Link
                to={`${PATHS.SETTINGS}?${ESettingsQueryParams.SECTION}=${ESettingsSection.PROFILE}&${ESettingsQueryParams.TAB}=${EProfileTab.BILLING}`}
              >
                <>
                  <CreditCard className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {t("user_menu.billing")}
                </>
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            id="faq-menu-item"
            data-testid="user-menu-faq"
            className={USER_MENU_SECTION_START_CLASS}
            asChild
          >
            <Link to={PATHS.FAQ}>
              <>
                <CircleHelp className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("user_menu.faq")}
              </>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            id="docs-menu-item"
            data-testid="user-menu-docs"
            className={USER_MENU_ROW_CLASS}
            asChild
          >
            {/* NOTE: Plain <a>, not Link — /docs is proxied to the
                Docusaurus site behind forward_auth, not an SPA route, so
                this must be a full-page navigation. Opens in a new tab so
                users don't lose their current chat/page context. */}
            <a href={PATHS.DOCS} target="_blank" rel="noopener noreferrer">
              <>
                <BookOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("user_menu.docs")}
              </>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="user-menu-logout"
            className={cn(
              USER_MENU_SECTION_START_CLASS,
              "text-destructive hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive",
            )}
            onClick={handleLogoutClick}
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t("user_menu.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <LogoutConfirmationModal
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
});
